import { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform, useSpring, useMotionValue } from "framer-motion";
import { setHeroProgress } from "../lib/heroLoader";

const FRAME_COUNT = 181;
const PUB = process.env.PUBLIC_URL || "";
const frameUrl = (i) => `${PUB}/frames/ezgif-frame-${String(i).padStart(3, "0")}.jpg`;
const HERO_FINAL = `${PUB}/assets/hero-helmet-final.png`;

function drawCover(ctx, img, cw, ch) {
  const ir = img.width / img.height;
  const cr = cw / ch;
  let dw, dh, dx, dy;
  if (cr > ir) {
    dw = cw;
    dh = cw / ir;
    dx = 0;
    dy = (ch - dh) / 2;
  } else {
    dh = ch;
    dw = ch * ir;
    dy = 0;
    dx = (cw - dw) / 2;
  }
  ctx.clearRect(0, 0, cw, ch);
  ctx.drawImage(img, dx, dy, dw, dh);
}

export default function HeroSequence() {
  const ref = useRef(null);
  const canvasRef = useRef(null);
  const imagesRef = useRef([]);
  const lastIndexRef = useRef(-1);
  const [loaded, setLoaded] = useState(0);
  const [ready, setReady] = useState(false);
  const [isMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  );

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });
  const p = useSpring(scrollYProgress, { stiffness: 170, damping: 30, mass: 0.25 });

  // Cursor-following crimson glow (wow factor)
  const glowX = useMotionValue(-1000);
  const glowY = useMotionValue(-1000);
  const gx = useSpring(glowX, { stiffness: 120, damping: 22, mass: 0.4 });
  const gy = useSpring(glowY, { stiffness: 120, damping: 22, mass: 0.4 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onMove = (e) => {
      glowX.set(e.clientX);
      glowY.set(e.clientY);
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => window.removeEventListener("pointermove", onMove);
    // eslint-disable-next-line
  }, []);

  // Preload all frames
  useEffect(() => {
    let count = 0;
    const imgs = new Array(FRAME_COUNT);
    for (let i = 0; i < FRAME_COUNT; i++) {
      const img = new Image();
      img.src = frameUrl(i + 1);
      img.onload = img.onerror = () => {
        count += 1;
        setLoaded(count);
        setHeroProgress(count / FRAME_COUNT);
        if (i === 0) setReady(true);
      };
      imgs[i] = img;
    }
    imagesRef.current = imgs;
  }, []);

  // Canvas sizing (retina-aware)
  useEffect(() => {
    const resize = () => {
      const c = canvasRef.current;
      if (!c) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      c.width = window.innerWidth * dpr;
      c.height = window.innerHeight * dpr;
      renderFrame(lastIndexRef.current < 0 ? 0 : lastIndexRef.current);
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
    // eslint-disable-next-line
  }, [ready]);

  const renderFrame = (idx) => {
    const c = canvasRef.current;
    const img = imagesRef.current[idx];
    if (!c || !img || !img.complete || img.naturalWidth === 0) return;
    const ctx = c.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    drawCover(ctx, img, c.width, c.height);
  };

  // Map scroll -> frame (scrub over first ~60% of the section)
  useEffect(() => {
    const unsub = p.on("change", (v) => {
      const scrub = Math.min(1, v / 0.6);
      const idx = Math.min(FRAME_COUNT - 1, Math.round(scrub * (FRAME_COUNT - 1)));
      if (idx !== lastIndexRef.current) {
        lastIndexRef.current = idx;
        requestAnimationFrame(() => renderFrame(idx));
      }
    });
    return () => unsub();
    // eslint-disable-next-line
  }, [p, ready]);

  useEffect(() => {
    if (ready) renderFrame(0);
    // eslint-disable-next-line
  }, [ready]);

  // Handoff: fade canvas out into the transparent cutout at the end of the scrub
  const canvasOpacity = useTransform(p, [0.55, 0.62], [1, 0]);
  const cutoutOpacity = useTransform(p, [0.55, 0.62], [0, 1]);
  const cutoutScale = useTransform(p, [0.62, 1], [1, 1.03]);

  // TOJI rises from behind the helmet and settles into the final lockup
  const tojiY = useTransform(p, [0.6, 0.9], ["12vh", "-4vh"]);
  const tojiOpacity = useTransform(p, [0.6, 0.72], [0, 1]);
  const tojiScale = useTransform(p, [0.6, 0.9], [0.88, 1]);
  const taglineOpacity = useTransform(p, [0.92, 1], [0, 1]);

  // Scroll cue only at the very top
  const cueOpacity = useTransform(p, [0, 0.05], [1, 0]);

  return (
    <section ref={ref} id="hero" data-testid="hero" className="relative h-[520vh] bg-[#07060a]">
      <div className="sticky top-0 h-screen w-full overflow-hidden bg-[#07060a]">
        {/* Cursor-following crimson glow */}
        <motion.div
          aria-hidden="true"
          style={{ x: gx, y: gy, marginLeft: "-21vw", marginTop: "-21vw" }}
          className="pointer-events-none fixed left-0 top-0 z-[27] h-[42vw] w-[42vw] rounded-full bg-[#d91616] opacity-[0.10] blur-[130px] mix-blend-screen"
        />

        {/* TOJI wordmark — behind the helmet on desktop; outlined overlay in front on mobile (always readable) */}
        <motion.div
          style={{ y: tojiY, opacity: tojiOpacity, scale: tojiScale }}
          className={`absolute inset-0 flex items-center justify-center ${isMobile ? "z-[24]" : "z-[10]"}`}
        >
          <h1
            data-testid="hero-title"
            className={`font-display text-[30vw] font-black leading-none tracking-[-0.03em] md:text-[27vw] ${
              isMobile ? "text-transparent [-webkit-text-stroke:2px_rgba(255,255,255,0.95)]" : "text-white"
            }`}
            style={{
              textShadow: isMobile
                ? "0 0 45px rgba(217,22,22,0.75)"
                : "0 0 100px rgba(217,22,22,0.5)",
            }}
          >
            TOJI
          </h1>
        </motion.div>

        {/* Seamless static cutout of the final frame (helmet in front of TOJI) */}
        <motion.div
          style={{ opacity: cutoutOpacity, scale: cutoutScale }}
          className="absolute inset-0 z-[20] flex items-center justify-center"
        >
          <img
            src={HERO_FINAL}
            alt="Toji samurai helmet"
            className="h-full w-full object-cover [filter:contrast(1.08)_saturate(1.07)_brightness(1.02)]"
            draggable={false}
          />
        </motion.div>

        {/* Scroll-scrubbed video frames — cinematic intro push-in on load */}
        <motion.div
          initial={{ opacity: 0, scale: 1.09 }}
          animate={ready ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 1.7, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0 z-[30]"
        >
          <motion.canvas
            ref={canvasRef}
            style={{ opacity: canvasOpacity }}
            className="absolute inset-0 h-full w-full [filter:contrast(1.08)_saturate(1.07)_brightness(1.02)]"
            data-testid="hero-canvas"
          />
        </motion.div>

        {/* Cinematic vignette + depth grade (masks source softness, adds premium feel) */}
        <div
          className="pointer-events-none absolute inset-0 z-[33]"
          style={{
            background:
              "radial-gradient(115% 85% at 50% 40%, transparent 42%, rgba(5,5,5,0.35) 78%, rgba(5,5,5,0.72) 100%)",
          }}
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 z-[33] h-1/3"
          style={{ background: "linear-gradient(to top, rgba(5,5,5,0.55), transparent)" }}
        />

        {/* Tagline at the end */}
        <motion.div
          style={{ opacity: taglineOpacity }}
          className="absolute inset-x-0 bottom-[12vh] z-[40] flex flex-col items-center px-6 text-center"
        >
          <p className="max-w-md font-body text-sm text-white/60 md:text-base">
            Research-grade peptides forged for those who refuse to break.
          </p>
        </motion.div>

        {/* Scroll cue */}
        <motion.div
          style={{ opacity: cueOpacity }}
          className="absolute bottom-5 left-1/2 z-[40] -translate-x-1/2"
        >
          <div className="flex flex-col items-center gap-2">
            <span className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-white/40">
              Scroll to awaken
            </span>
            <div className="h-8 w-[1px] bg-gradient-to-b from-[#d91616] to-transparent" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
